import type { ProductInfoData } from "@/components/ui/product-info-modal"
import { MRI_PACKAGE_INFO, ADDON_INFO } from "@/data/product-info"
import { PRODUCT_IDS } from "@/lib/booking"
import type { StepId } from "./questions"
import type { AddOnId, BaseTierId, Gender } from "./types"

export function baseTierInfo(baseTier: BaseTierId): ProductInfoData {
  switch (baseTier) {
    case "blood_only":
      return ADDON_INFO.advancedBloodTest
    case "advanced_mri":
      return MRI_PACKAGE_INFO.Advanced
    case "pro_mri":
      return MRI_PACKAGE_INFO.Pro
  }
}

export function baseTierBookingProductId(baseTier: BaseTierId): string {
  switch (baseTier) {
    case "blood_only":
      return PRODUCT_IDS.bloodTest
    case "advanced_mri":
      return PRODUCT_IDS.advanced
    case "pro_mri":
      return PRODUCT_IDS.pro
  }
}

/**
 * Map a quiz AddOnId to the Supabase product UUID the booking app
 * uses to add it to the cart.
 *
 * UUIDs sourced from the `ahead-booking-new` repo's Supabase seeds —
 * `supabase/seeds/01-base-data.sql` and `06-blood-only.sql`. Heart-CT
 * calcium scoring is reactivated by `03-dexa-and-ct.sql` (the row
 * shipped archived in `01-base-data.sql`, then gets `status = 'active'`
 * via the seed-3 update).
 *
 * Returns `null` for add-ons that don't map to a separate booking sku:
 * - `trend_overlay`: free analysis layer, no separate product
 * - `brain_interest` is folded into the Pro tier upgrade, not a
 *   standalone add-on, so it never reaches this map.
 */
export function addOnBookingProductId(
  id: AddOnId,
  gender: Gender | undefined
): string | null {
  switch (id) {
    case "dexa_scan":
      return "44cdf268-5771-45bd-8b41-c6f2edbacdd9"
    case "mamma_mri":
      return "7d91c0da-54c2-434b-ade4-9b03eb2108fd"
    case "calcium_score":
      return "43e40f10-61c2-49d4-a4f4-61a51ae004f4"
    case "hormone_test":
      return gender === "female"
        ? "e57e0619-d6bf-4f27-9e42-cecc4ebcaabb" // Hormone blood test (Women)
        : "a4db0619-d6bf-4f27-9e42-cecc4ebcaacc" // Hormone blood test (Men)
    case "b_vitamins":
      return "bf00dc19-d6bf-4f27-9e42-cecc4ebcaaaa"
    case "omega_test":
      return "fa771619-d6bf-4f27-9e42-cecc4ebcaadd"
    case "trend_overlay":
      return null
  }
}

/** Optional ProductInfoData to surface next to an interest-question's title.
 * Returns null for steps that don't have a corresponding /pricing product
 * (age, gender, trend overlay, insurance, email). The MRI step shows its own
 * info next to the main yes/no question — the imaging follow-ups (brain,
 * calcium, dexa, mamma) render their info buttons inline per checkbox. */
export function stepInfo(
  step: StepId,
  gender: Gender | undefined
): ProductInfoData | null {
  switch (step) {
    case "hormone":
      return gender === "female"
        ? ADDON_INFO.addonHormoneWomen
        : ADDON_INFO.addonHormoneMen
    case "omega":
      return ADDON_INFO.addonOmega
    case "b_vitamins":
      return ADDON_INFO.addonBVitamins
    case "mri":
      return MRI_PACKAGE_INFO.Advanced
    case "age":
    case "gender":
    case "trend":
    // mri_addons renders its own info button per follow-up tile, no
    // single product to surface next to the page-level title.
    case "mri_addons":
    case "insurance":
    case "email":
      return null
  }
}

export function addOnInfo(
  id: AddOnId,
  gender: Gender | undefined
): ProductInfoData | null {
  switch (id) {
    case "hormone_test":
      return gender === "female"
        ? ADDON_INFO.addonHormoneWomen
        : ADDON_INFO.addonHormoneMen
    case "omega_test":
      return ADDON_INFO.addonOmega
    case "b_vitamins":
      return ADDON_INFO.addonBVitamins
    case "calcium_score":
      return ADDON_INFO.addonHeartCT
    case "dexa_scan":
      return ADDON_INFO.addonDEXA
    case "mamma_mri":
      return ADDON_INFO.addonMammaMRI
    case "trend_overlay":
      return null
  }
}
